import { hashKey } from '@tanstack/react-query';
import {
  restockBatchSendInput,
  reorderDraftsInput,
  restockBatchSentResult,
  reorderDraftsResult,
  restockSendInput,
  type RestockBatchSendInput,
  type ReorderDraftsInput,
} from '@servwell/shared';
import { sameAccount, type AccountRef } from './account';

type Labels = {
  vans: { vanId: string; name: string; items: { itemId: string; name: string }[] }[];
  buys: { feederId: string; itemId: string; name: string; destinationName: string }[];
};
export type ReorderWriteAttempt =
  | {
      action: 'send';
      request: RestockBatchSendInput;
      accepted?: boolean;
      receipt?: ReturnType<typeof restockBatchSentResult.parse>;
      labels?: Labels;
    }
  | {
      action: 'draft';
      request: ReorderDraftsInput;
      accepted?: boolean;
      receipt?: ReturnType<typeof reorderDraftsResult.parse>;
      labels?: Labels;
    };
function parseAttempt(value: unknown): ReorderWriteAttempt | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (
    Object.keys(v).some(
      (k) => !['action', 'request', 'accepted', 'receipt', 'labels'].includes(k),
    )
  )
    return null;
  if (v.action !== 'send' && v.action !== 'draft') return null;
  const request = (
    v.action === 'send' ? restockBatchSendInput : reorderDraftsInput
  ).safeParse(v.request);
  if (!request.success || (v.accepted !== undefined && typeof v.accepted !== 'boolean'))
    return null;
  const receipt =
    v.receipt === undefined
      ? undefined
      : (v.action === 'send' ? restockBatchSentResult : reorderDraftsResult).safeParse(
          v.receipt,
        );
  if (receipt && !receipt.success) return null;
  // Display labels are bounded text only; quantities and receipts come from strict shared contracts.
  const text = (x: unknown) => typeof x === 'string' && x.length <= 500;
  if (v.labels !== undefined) {
    const l = v.labels as Labels;
    if (
      !l ||
      !Array.isArray(l.vans) ||
      !Array.isArray(l.buys) ||
      l.vans.length > 200 ||
      l.buys.length > 200 ||
      l.vans.some(
        (v) =>
          !v ||
          !text(v.vanId) ||
          !text(v.name) ||
          !Array.isArray(v.items) ||
          v.items.length > 5000 ||
          v.items.some((i) => !i || !text(i.itemId) || !text(i.name)),
      ) ||
      l.buys.some(
        (b) =>
          !b ||
          !text(b.feederId) ||
          !text(b.itemId) ||
          !text(b.name) ||
          !text(b.destinationName),
      )
    )
      return null;
  }
  return {
    action: v.action,
    request: request.data,
    ...(v.accepted === undefined ? {} : { accepted: v.accepted }),
    ...(receipt?.success ? { receipt: receipt.data } : {}),
    ...(v.labels === undefined ? {} : { labels: v.labels }),
  } as ReorderWriteAttempt;
}
export type ReorderWriteRead =
  | { status: 'empty' }
  | { status: 'unreadable' }
  | { status: 'ready'; attempt: ReorderWriteAttempt };
const slot = (owner: AccountRef) =>
  `servwell-reorder-write:${owner.orgId}:${owner.userId}`;
export const legacyRestockSendKey = (owner: AccountRef, vanId: string) =>
  `servwell-restock-send:${owner.orgId}:${owner.userId}:${vanId}`;

/** One same-origin account lock: conflict check + durable claim, never a network/user wait. */
export async function withOfficeInventoryLock<T>(
  owner: AccountRef,
  run: () => T,
): Promise<T> {
  if (typeof navigator === 'undefined' || !navigator.locks?.request)
    throw new Error(
      'This browser cannot safely coordinate saved inventory requests. Keep the saved request and reload with browser storage available.',
    );
  return navigator.locks.request(
    `servwell-office-inventory:${JSON.stringify([owner.orgId, owner.userId])}`,
    { mode: 'exclusive' },
    () => run(),
  );
}
export function readReorderWrite(owner: AccountRef): ReorderWriteRead {
  try {
    const raw = localStorage.getItem(slot(owner));
    if (raw === null) return { status: 'empty' };
    const value = JSON.parse(raw);
    const attempt = parseAttempt(value?.attempt);
    if (
      !attempt ||
      !sameAccount(value.owner, owner) ||
      Object.keys(value).some((k) => !['owner', 'attempt'].includes(k))
    )
      return { status: 'unreadable' };
    return { status: 'ready', attempt };
  } catch {
    return { status: 'unreadable' };
  }
}
/** Exact legacy keys are known from the selected vans; never scan another account's storage. */
function legacySlotOccupied(owner: AccountRef, vanId: string) {
  const raw = localStorage.getItem(legacyRestockSendKey(owner, vanId));
  return raw !== null; // Unreadable legacy payloads also block; the original single-van view resolves them.
}
export function hasBlockingReorderWrite(owner: AccountRef, vanId: string) {
  const current = readReorderWrite(owner);
  return (
    current.status === 'unreadable' ||
    (current.status === 'ready' &&
      current.attempt.action === 'send' &&
      current.attempt.request.sends.some((v) => v.vanId === vanId))
  );
}
const sameRequest = (a: ReorderWriteAttempt, b: ReorderWriteAttempt) =>
  a.action === b.action && hashKey([a.request]) === hashKey([b.request]);
export async function reserveReorderWrite(
  owner: AccountRef,
  raw: ReorderWriteAttempt,
): Promise<boolean> {
  try {
    return await withOfficeInventoryLock(owner, () => {
      const incoming = parseAttempt(raw);
      if (!incoming) return false;
      const current = readReorderWrite(owner);
      if (current.status === 'unreadable') return false;
      if (current.status === 'ready') return sameRequest(current.attempt, incoming);
      if (
        incoming.action === 'send' &&
        incoming.request.sends.some((v) => legacySlotOccupied(owner, v.vanId))
      )
        return false;
      // The caller cannot mint an accepted receipt; only the response path can add it.
      const { accepted: _accepted, receipt: _receipt, ...candidate } = incoming;
      localStorage.setItem(slot(owner), JSON.stringify({ owner, attempt: candidate }));
      const written = readReorderWrite(owner);
      return written.status === 'ready' && sameRequest(written.attempt, candidate);
    });
  } catch {
    return false;
  }
}
export async function acceptReorderWrite(
  owner: AccountRef,
  attempt: ReorderWriteAttempt,
  receipt: unknown,
): Promise<boolean> {
  try {
    return await withOfficeInventoryLock(owner, () => {
      const current = readReorderWrite(owner);
      if (current.status !== 'ready' || !sameRequest(current.attempt, attempt))
        return false;
      const parsed =
        attempt.action === 'send'
          ? restockBatchSentResult.safeParse(receipt)
          : reorderDraftsResult.safeParse(receipt);
      if (!parsed.success) return false;
      localStorage.setItem(
        slot(owner),
        JSON.stringify({
          owner,
          attempt: { ...current.attempt, accepted: true, receipt: parsed.data },
        }),
      );
      const written = readReorderWrite(owner);
      return (
        written.status === 'ready' &&
        written.attempt.accepted === true &&
        sameRequest(written.attempt, attempt)
      );
    });
  } catch {
    return false;
  }
}
export async function clearReorderWrite(
  owner: AccountRef,
  mutationId: string,
): Promise<boolean> {
  try {
    return await withOfficeInventoryLock(owner, () => {
      const current = readReorderWrite(owner);
      if (current.status === 'empty') return true;
      if (current.status !== 'ready' || current.attempt.request.mutationId !== mutationId)
        return false;
      localStorage.removeItem(slot(owner));
      return readReorderWrite(owner).status === 'empty';
    });
  } catch {
    return false;
  }
}
/** Called only after a distinct manager confirmation; never removes a readable request. */
export async function clearUnreadableReorderWrite(owner: AccountRef): Promise<boolean> {
  try {
    return await withOfficeInventoryLock(owner, () => {
      const current = readReorderWrite(owner);
      if (current.status === 'empty') return true;
      if (current.status !== 'unreadable') return false;
      localStorage.removeItem(slot(owner));
      return readReorderWrite(owner).status === 'empty';
    });
  } catch {
    return false;
  }
}
/** Legacy payload reader shared with its adapter; preserved key and accepted-flag format. */
export function parseLegacyRestockSend(raw: string) {
  const value = JSON.parse(raw);
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { accepted, ...request } = value as Record<string, unknown>;
  const parsed = restockSendInput.safeParse(request);
  return parsed.success ? { attempt: parsed.data, accepted: accepted === true } : null;
}
